import { useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Controller, FormProvider, useForm, useWatch } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  Button,
  Card,
  CircularProgress,
  FormControlLabel,
  Stack,
  Switch,
  Typography,
} from '@mui/material'
import { toast } from 'react-toastify'
import { FormBlock } from '../../../../common/FormGenerator'
import { getFormConfig } from './settingsConfig'
import { editSocialOrderFormSchema } from '../../CreateSocialOrder/ZodValidationSchema'
import type { EditSocialOrderFormValues } from '../../CreateSocialOrder/ZodValidationSchema'
import { CardStyle, TypStyle } from './style'
import { useGetSocialOrderQuery, useUpdateSocialOrderMutation } from '../../../../api/socialOrders'
import { extractApiErrorMessage } from '../../../../utils/api'
import { isSocialOrderFormUnchanged } from '../../../../utils/socialOrder/areSocialOrderFormOutputsEqual'

const mapInitialValues = (data: {
  title: string
  description: string
  problem: string | null
  focus: string[]
  is_completed: boolean
  completion_description: string | null
  completion_resource_url: string | null
}): EditSocialOrderFormValues => ({
  title: data.title,
  description: data.description,
  problem: data.problem ?? '',
  focus: Array.isArray(data.focus) ? data.focus : [],
  is_completed: Boolean(data.is_completed),
  completion_description: data.completion_description ?? '',
  completion_resource_url: data.completion_resource_url ?? '',
})

export const EditSocialOrderForm = () => {
  const { id } = useParams<{ id: string }>()
  const orderId = id ? parseInt(id, 10) : NaN
  const { data: initialData } = useGetSocialOrderQuery({ id: orderId }, { skip: Number.isNaN(orderId) })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [updateSocialOrder] = useUpdateSocialOrderMutation()
  const baselineRef = useRef<EditSocialOrderFormValues | null>(null)

  const methods = useForm<EditSocialOrderFormValues>({
    mode: 'onBlur',
    resolver: zodResolver(editSocialOrderFormSchema),
    defaultValues: {
      title: '',
      description: '',
      problem: '',
      focus: [],
      is_completed: false,
      completion_description: '',
      completion_resource_url: '',
    },
  })

  const { handleSubmit, reset, control } = methods
  const isCompleted = useWatch({ control, name: 'is_completed' })

  useEffect(() => {
    if (!initialData) return
    const values = mapInitialValues(initialData)
    reset(values)
    baselineRef.current = values
  }, [initialData, reset])

  const onSubmit = async (data: EditSocialOrderFormValues) => {
    if (Number.isNaN(orderId)) return

    if (baselineRef.current != null && isSocialOrderFormUnchanged(data, baselineRef.current)) {
      toast.info('Нет изменений')
      return
    }

    setIsSubmitting(true)
    try {
      const completionDescription = data.completion_description.trim()
      const completionResourceUrl = data.completion_resource_url.trim()

      await updateSocialOrder({
        id: orderId,
        payload: {
          title: data.title,
          description: data.description,
          focus: data.focus,
          problem: data.problem,
          is_completed: data.is_completed,
          ...(data.is_completed
            ? {
                completion_description: completionDescription,
                completion_resource_url: completionResourceUrl,
              }
            : {
                ...(completionDescription ? { completion_description: completionDescription } : {}),
                ...(completionResourceUrl ? { completion_resource_url: completionResourceUrl } : {}),
              }),
        },
      }).unwrap()

      baselineRef.current = data
      toast.success('Социальный заказ сохранён')
    } catch (error) {
      toast.error(extractApiErrorMessage(error) ?? 'Не удалось сохранить изменения')
    } finally {
      setIsSubmitting(false)
    }
  }

  if (!initialData) {
    return null
  }

  return (
    <FormProvider {...methods}>
      <Stack gap={2} component="form" onSubmit={handleSubmit(onSubmit)}>
        <Card variant="primaryAdaptiveCard" sx={CardStyle}>
          <Typography variant="black-16-semiBold" component="div" sx={TypStyle}>
            Название
          </Typography>
          <FormBlock formFieldConfig={getFormConfig('title')} />
        </Card>
        <Card variant="primaryAdaptiveCard" sx={CardStyle}>
          <Typography variant="black-16-semiBold" component="div" sx={TypStyle}>
            Описание
          </Typography>
          <FormBlock formFieldConfig={getFormConfig('description')} />
        </Card>
        <Card variant="primaryAdaptiveCard" sx={CardStyle}>
          <Typography variant="black-16-semiBold" component="div" sx={TypStyle}>
            Проблема, которую решает социальный заказ
          </Typography>
          <FormBlock formFieldConfig={getFormConfig('problem')} />
        </Card>
        <Card variant="primaryAdaptiveCard" sx={CardStyle}>
          <Typography variant="black-16-semiBold" component="div" sx={TypStyle}>
            Направленность
          </Typography>
          <FormBlock formFieldConfig={getFormConfig('focus')} />
        </Card>
        <Card variant="primaryAdaptiveCard" sx={CardStyle}>
          <Typography variant="black-16-semiBold" component="div" sx={TypStyle}>
            Статус выполнения
          </Typography>
          <Controller
            name="is_completed"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={
                  <Switch
                    checked={field.value}
                    onChange={(event) => field.onChange(event.target.checked)}
                  />
                }
                label="Выполнен"
              />
            )}
          />
          {isCompleted ? (
            <Stack gap={2} mt={1}>
              <FormBlock formFieldConfig={getFormConfig('completion_description')} />
              <FormBlock formFieldConfig={getFormConfig('completion_resource_url')} />
            </Stack>
          ) : null}
        </Card>

        <Stack direction="row" spacing={1}>
          <Button type="submit" variant="follow" disabled={isSubmitting}>
            {isSubmitting ? (
              <Stack direction="row" alignItems="center" spacing={1}>
                <CircularProgress size={16} color="inherit" />
                <span>Сохранение...</span>
              </Stack>
            ) : (
              'Сохранить'
            )}
          </Button>
        </Stack>
      </Stack>
    </FormProvider>
  )
}
